import { create } from 'zustand';
import type { InvoiceStatus } from '@shared/types';
import { useShellStore } from '@renderer/app/state/shell-store';
import { countHiddenSelectedIds, removeSelectedId, toggleSelectionIds } from '@renderer/shared/utils/collection-selection';

export type InvoicesSort = 'issueDate' | 'dueDate' | 'number';
export type InvoiceDueFilter = 'all' | 'open' | 'overdue' | 'paid';
export type InvoiceEditorTab = 'general' | 'lineItems' | 'document';

interface InvoicesSelectionSession {
  selectedIds: string[];
  anchorId: string | null;
  queryKey: string;
  hiddenSelectedCount: number;
}

function createInitialSelectionSession(): InvoicesSelectionSession {
  return {
    selectedIds: [],
    anchorId: null,
    queryKey: '',
    hiddenSelectedCount: 0
  };
}

interface InvoicesViewStore {
  statusFilter: 'all' | InvoiceStatus;
  customerFilter: 'all' | string;
  issuerFilter: 'all' | string;
  dueFilter: InvoiceDueFilter;
  sort: InvoicesSort;
  editorTab: InvoiceEditorTab;
  selectionSession: InvoicesSelectionSession;
  setStatusFilter: (value: 'all' | InvoiceStatus) => void;
  setCustomerFilter: (value: 'all' | string) => void;
  setIssuerFilter: (value: 'all' | string) => void;
  setDueFilter: (value: InvoiceDueFilter) => void;
  setSort: (value: InvoicesSort) => void;
  setEditorTab: (value: InvoiceEditorTab) => void;
  setViewMode: (value: 'grid' | 'list') => Promise<void>;
  syncSelectionQuery: (queryKey: string, visibleIds: string[]) => void;
  toggleSelection: (
    itemId: string,
    orderedIds: string[],
    queryKey: string,
    options?: {
      additive?: boolean;
      range?: boolean;
    }
  ) => void;
  removeSelectedId: (itemId: string, visibleIds: string[]) => void;
  clearSelection: () => void;
}

export const useInvoicesViewStore = create<InvoicesViewStore>((set) => ({
  statusFilter: 'all',
  customerFilter: 'all',
  issuerFilter: 'all',
  dueFilter: 'all',
  sort: 'issueDate',
  editorTab: 'general',
  selectionSession: createInitialSelectionSession(),
  setStatusFilter: (statusFilter) => set({ statusFilter }),
  setCustomerFilter: (customerFilter) => set({ customerFilter }),
  setIssuerFilter: (issuerFilter) => set({ issuerFilter }),
  setDueFilter: (dueFilter) => set({ dueFilter }),
  setSort: (sort) => set({ sort }),
  setEditorTab: (editorTab) => set({ editorTab }),
  setViewMode: async (invoicesViewMode) => {
    await useShellStore.getState().savePreferences({ invoicesViewMode });
  },
  syncSelectionQuery: (queryKey, visibleIds) =>
    set((state) => {
      const hiddenSelectedCount = countHiddenSelectedIds(state.selectionSession.selectedIds, visibleIds);

      if (state.selectionSession.queryKey === queryKey && state.selectionSession.hiddenSelectedCount === hiddenSelectedCount) {
        return state;
      }

      return {
        selectionSession: {
          ...state.selectionSession,
          queryKey,
          hiddenSelectedCount
        }
      };
    }),
  toggleSelection: (itemId, orderedIds, queryKey, options) =>
    set((state) => {
      const nextSelection = toggleSelectionIds({
        selectedIds: state.selectionSession.selectedIds,
        anchorId: state.selectionSession.anchorId,
        itemId,
        orderedIds,
        additive: options?.additive ?? false,
        range: options?.range ?? false
      });

      return {
        selectionSession: {
          ...state.selectionSession,
          ...nextSelection,
          queryKey,
          hiddenSelectedCount: countHiddenSelectedIds(nextSelection.selectedIds, orderedIds)
        }
      };
    }),
  removeSelectedId: (itemId, visibleIds) =>
    set((state) => {
      const selectedIds = removeSelectedId(state.selectionSession.selectedIds, itemId);

      return {
        selectionSession: {
          ...state.selectionSession,
          selectedIds,
          anchorId: state.selectionSession.anchorId === itemId ? null : state.selectionSession.anchorId,
          hiddenSelectedCount: countHiddenSelectedIds(selectedIds, visibleIds)
        }
      };
    }),
  clearSelection: () =>
    set((state) => ({
      selectionSession: {
        ...createInitialSelectionSession(),
        queryKey: state.selectionSession.queryKey
      }
    }))
}));
